import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { SxProps, Theme } from '@mui/material';
import ReactGA from 'react-ga4';
import { useColors } from '../theme/ColorModeContext';
import { DISCORD_INVITE_URL } from './brand';

// Canonical Discord glyph, also used by the TopBar social row.
export const DiscordIcon = ({ size = 18 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="m22 24l-5.25-5l.63 2H4.5A2.5 2.5 0 0 1 2 18.5v-15A2.5 2.5 0 0 1 4.5 1h15A2.5 2.5 0 0 1 22 3.5V24M12 6.8c-2.68 0-4.56 1.15-4.56 1.15c1.03-.92 2.83-1.45 2.83-1.45l-.17-.17c-1.69.03-3.22 1.2-3.22 1.2c-1.72 3.59-1.61 6.69-1.61 6.69c1.4 1.81 3.48 1.68 3.48 1.68l.71-.9c-1.25-.27-2.04-1.38-2.04-1.38S9.3 14.9 12 14.9s4.58-1.28 4.58-1.28s-.79 1.11-2.04 1.38l.71.9s2.08.13 3.48-1.68c0 0 .11-3.1-1.61-6.69c0 0-1.53-1.17-3.22-1.2l-.17.17s1.8.53 2.83 1.45c0 0-1.88-1.15-4.56-1.15m-2.07 3.79c.65 0 1.18.57 1.17 1.27c0 .69-.52 1.27-1.17 1.27c-.64 0-1.16-.58-1.16-1.27c0-.7.51-1.27 1.16-1.27m4.17 0c.65 0 1.17.57 1.17 1.27c0 .69-.52 1.27-1.17 1.27c-.64 0-1.16-.58-1.16-1.27c0-.7.51-1.27 1.16-1.27Z" />
    </svg>
);

// Best-effort conversion tracking. `placement` distinguishes where the click
// came from so we can compare which surfaces actually drive Discord joins.
export const trackDiscordClick = (placement: string) => {
    try {
        ReactGA.event({ category: 'discord', action: 'join_click', label: placement });
    } catch {
        // analytics is non-critical: never let it break the click
    }
};

type DiscordCTAVariant = 'reward' | 'inline' | 'tile';

interface DiscordCTAProps {
    variant: DiscordCTAVariant;
    // Identifier for analytics, e.g. "challenge-complete", "stage-complete", "challenge-console", "dashboard".
    placement: string;
    headline?: string;
    body?: string;
    sx?: SxProps<Theme>;
}

const DEFAULT_COPY: Record<DiscordCTAVariant, { headline: string; body: string }> = {
    reward: {
        headline: '',
        body: 'Cleared it. Compare solutions and get hints for what\'s next in the Web3Pwn Discord.',
    },
    inline: {
        headline: '',
        body: 'Stuck? Trade hints with other players in the Web3Pwn Discord.',
    },
    tile: {
        headline: 'Community',
        body: 'Coordinate and compare runs on Discord.',
    },
};

const DiscordCTA = ({ variant, placement, headline, body, sx }: DiscordCTAProps) => {
    const colors = useColors();
    const copy = DEFAULT_COPY[variant];
    const resolvedHeadline = headline ?? copy.headline;
    const resolvedBody = body ?? copy.body;

    const linkProps = {
        href: DISCORD_INVITE_URL,
        target: '_blank',
        rel: 'noopener noreferrer',
        onClick: () => trackDiscordClick(placement),
    };

    if (variant === 'inline') {
        return (
            <Box
                component="a"
                {...linkProps}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    textDecoration: 'none',
                    color: colors.text.muted,
                    fontSize: '0.82rem',
                    fontFamily: '"Exo 2", sans-serif',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: colors.accent.cyan },
                    ...sx as any,
                }}
            >
                <DiscordIcon size={15} />
                <span>{resolvedBody}</span>
                <Box component="span" aria-hidden="true">&rarr;</Box>
            </Box>
        );
    }

    if (variant === 'tile') {
        return (
            <Box
                component="a"
                {...linkProps}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border.subtle}`,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        borderColor: colors.accent.cyan,
                        boxShadow: `0 0 16px ${colors.accent.cyanGlow}`,
                    },
                    ...sx as any,
                }}
            >
                <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    backgroundColor: colors.accent.cyanGlow,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: colors.accent.cyan,
                }}>
                    <DiscordIcon size={20} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        fontFamily: '"Orbitron", sans-serif',
                        color: colors.text.primary,
                        marginBottom: '2px',
                    }}>
                        {resolvedHeadline}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: colors.text.secondary }}>
                        {resolvedBody}
                    </Typography>
                </Box>
                <Box component="span" aria-hidden="true" sx={{ color: colors.text.muted, fontSize: '14px' }}>&rarr;</Box>
            </Box>
        );
    }

    // variant === 'reward'
    return (
        <Box sx={{
            p: '20px',
            borderRadius: '8px',
            border: `1px solid ${colors.border.subtle}`,
            backgroundColor: colors.bg.tertiary,
            textAlign: 'center',
            ...sx as any,
        }}>
            <Box sx={{ color: colors.accent.cyan, mb: '8px' }}>
                <DiscordIcon size={24} />
            </Box>
            {resolvedHeadline && (
                <Typography sx={{
                    color: colors.text.primary,
                    fontFamily: '"Orbitron", sans-serif',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    mb: '4px',
                }}>
                    {resolvedHeadline}
                </Typography>
            )}
            <Typography sx={{ color: colors.text.secondary, fontSize: '0.88rem', maxWidth: 380, margin: '0 auto 14px' }}>
                {resolvedBody}
            </Typography>
            <Button
                component="a"
                {...linkProps}
                variant="outlined"
                startIcon={<DiscordIcon size={16} />}
                sx={{
                    borderColor: colors.accent.cyan,
                    color: colors.accent.cyan,
                    fontFamily: '"Orbitron", sans-serif',
                    '&:hover': { borderColor: colors.accent.cyan, backgroundColor: colors.accent.cyanGlow },
                }}
            >
                Join the Discord
            </Button>
        </Box>
    );
};

export default DiscordCTA;
